import { Component, effect } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { Plan } from 'src/app/models/plan.model';
import { collections } from 'src/app/services/data/collections';
import { styles } from 'src/app/services/data/styles';
import imageCompression from 'browser-image-compression';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { SelectModule } from 'primeng/select';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { TooltipModule } from 'primeng/tooltip';
import { PlansService } from '@/services/api/plans.service';

interface EditablePlan {
  PlanID: number | string;
  PlanName: string;
  Family: string;
  StyleID: number;
  Suffix: string | null;
  Size: number;
  Floors: number;
  Garage: boolean;
  Available: boolean;
  Archived: boolean;
  Collection: string;
}

interface ImageUpdate {
  PlanID: number | string;  // Can be number for existing plans or string for temp IDs
  imageFile: File;
}

@Component({
    selector: 'app-plan-editor',
    imports: [
      CommonModule,
      ReactiveFormsModule,
      FormsModule,
      InputTextModule,
      ButtonModule,
      TableModule,
      OverlayPanelModule,
      ToggleButtonModule,
      ConfirmDialogModule,
      FileUploadModule,
      SelectModule,
      ConfirmPopupModule,
      TooltipModule
    ],
    template: `
    <form [formGroup]="planForm" (ngSubmit)="onSubmit()">
  <div class="flex justify-content-between align-items-center">
    <div formGroupName="family" class="p-1">
      <label for="familyName" class="mr-2">Family Name</label>
      <input pInputText id="familyName" formControlName="familyName" />
    </div>
    <p-button label="Add Plan" (click)="addPlan()" icon="pi pi-plus" [rounded]="true" [text]="true" />
  </div>
    <div formArrayName="plans">
        <p-table [value]="plans.controls" [tableStyle]="{ 'min-width': '50rem' }" [styleClass]="'p-datatable-sm'">
            <ng-template pTemplate="header">
              <tr>
                <th>Id</th>
                <th>Thumb</th>
                <th>Name</th>
                <th>Size</th>
                <th>Suffix</th>
                <th>Style</th>
                <th>Collection</th>
                <th>Floors</th>
                <th>Garage</th>
                <th>Status</th>
                <th></th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-plan let-i="rowIndex">
              <tr [formGroupName]="i">
                <td>{{ plan.get('PlanID').value }}</td>
                <td>
                  <p-button (onClick)="op.toggle($event)" icon="pi pi-image"/>
    <p-overlayPanel #op>
        <div class="flex flex-column gap-3 w-25rem">
            <div>
                <img [src]="getImageUrl(plan)" style="width: auto; height: 100px;"/>
            </div>
            <div>
              <p-fileUpload 
              mode="basic" 
              name="demo[]" 
              [chooseIcon]="fileProcessing ? 'pi pi-spinner pi-spin' : 'pi pi-upload'" 
              [customUpload]="true"
              accept="image/*" maxFileSize="50000000" 
              (uploadHandler)="onImageSelect(plan, $event)" 
              [disabled]="fileProcessing"
              [auto]="true" 
              chooseLabel="Browse"/>
            </div>
        </div>
    </p-overlayPanel>
                </td>
                <td>
                  <input type="text" pInputText formControlName="PlanName"/>
                </td>
                <td>
                  <input type="number" pInputText  formControlName="Size"  (onChange)="onSizeChange(plan, $event)" class="w-5rem no-spinners" />
                </td>
                <td>
                  <input type="text" pInputText formControlName="Suffix" (onChange)="onSuffixChange(plan, $event)" class="w-5rem" />
                </td>
                <td>
                  <p-select appendTo="body" [options]="styleOptions" optionLabel="StyleName" optionValue="StyleID" formControlName="StyleID" (onChange)="onStyleChange(plan, $event)" />
                </td>
                <td>
                  <p-select appendTo="body" [options]="collectionOptions" formControlName="Collection" (onChange)="onCollectionChange(plan, $event)" />
                </td>
                <td>
                  <input type="number" pInputText formControlName="Floors" max="9" min="1" class="w-5rem"/>
                </td>
                <td>
                    <p-toggleButton 
                    formControlName="Garage"
                    onIcon="pi pi-check"
                    offIcon="pi pi-times"
                    class="mr-1"
                    />
                </td>
                <td>
                    <p-toggleButton 
                    formControlName="Available"
                    onIcon="pi pi-lock-open"
                    offIcon="pi pi-lock"
                    class="mr-1"
                    [pTooltip]="plan.get('Available').value ? 'Available to all offices' : 'Only available to selected offices'"
    tooltipPosition="bottom" 
                    />
                    <p-toggleButton 
                    formControlName="Archived"
                    onIcon="pi pi-eye-slash"
                    offIcon="pi pi-eye"
                    [pTooltip]="plan.get('Archived').value ? 'Archived' : 'Not Archived'"
    tooltipPosition="bottom" 
                    />
                </td>
                <td>
                  <p-button icon="pi pi-trash" severity="danger" (click)="deletePlan(plan)" />
                </td>
                <p-confirmPopup />
              </tr>
            </ng-template>
          </p-table>
    </div>
    <p-button label="Save" (click)="onSubmit()" />
  </form>
    `,
    styles: [
    `
      /* Chrome, Safari, Edge, Opera */
.no-spinners::-webkit-outer-spin-button,
.no-spinners::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number].no-spinners {
  -moz-appearance: textfield;
}
      `
    ]
})
export class PlanEditorComponent {
  fileProcessing: boolean = false;
  private tempUrls: string[] = [];
  planForm!: FormGroup;
  avilablityOptions: any[] = [
    { icon: 'pi pi-lock', value: false },
    { icon: 'pi pi-lock-open', value: true }
];

private tempIdCounter = 0;
pendingImageUpdates: ImageUpdate[] = [];

archivedOptions: any[] = [
  { icon: 'pi pi-eye-slash', value: false },
    { icon: 'pi pi-eye', value: true }
];

collectionOptions: string[] = collections;

styleOptions:any[] = styles;

dummyPlan:EditablePlan = { PlanID: 1, PlanName: '1000-Plan-TRD', Family: 'Plan', StyleID: 1, Suffix: null, Size: 1000, Floors: 1, Garage: true, Available: true, Archived: false, Collection: 'Founders' };

  constructor(private fb: FormBuilder,public plansDataService: PlansService, private confirmationService: ConfirmationService) {
    effect(() => {
      const plan = this.plansDataService.selectedPlan();
      const allPlans = this.plansDataService.allPlansData();
      if(plan && allPlans) {
        this.setupForm(plan, allPlans);
      }
    });
  }

  ngOnInit() {
    this.planForm = this.fb.group({
      family: this.fb.group({
        familyId: [1, Validators.required],
        familyName: ['Family 1', Validators.required]
      }),
      plans: this.fb.array([
        this.createPlan(this.dummyPlan)
      ])
    });
  }

  getFamilyPlansByName(familyName: string, allPlans: Plan[]): Plan[] {
    return allPlans.filter(plan => plan.Family === familyName);
  }


  setupForm(selectedPlan: Plan, allPlans: Plan[]) {
    const familyName = selectedPlan.Family;
    const familyPlans = this.getFamilyPlansByName(familyName, allPlans);
    this.setDummyFamily();

    this.planForm.patchValue({
      family: {
        familyId: selectedPlan.familyId, // Assuming familyId is part of the selectedPlan
        familyName: familyName
      }
    });

    this.setPlans(familyPlans);
  }

  setPlans(plans: EditablePlan[]) {
    const planFormArray = this.planForm.get('plans') as FormArray;
    planFormArray.clear();
    plans.forEach(plan => {
      planFormArray.push(this.createPlan(plan));
    });
  }

  updateFamilyName(plan: FormGroup, name: string) {
    const oldName = plan.get('Family')?.value;
    plan.get('Family')?.setValue(name);
    this.plans.controls.find(p => p.get('Family')?.value === oldName)?.get('Family')?.setValue(name);
  }

  onStyleChange(plan: FormGroup, event: any) {
    plan.get('Styles')?.setValue(event.value);
    this.updatePlanName(plan);
  }

  onCollectionChange(plan: FormGroup, event: any) {
    plan.get('Collection')?.setValue(event.value);
  }

  onSizeChange(plan: FormGroup, event: any) {
    plan.get('Size')?.setValue(event.value);
    this.updatePlanName(plan);
  }

  onSuffixChange(plan: FormGroup, event: any) {
    plan.get('Suffix')?.setValue(event.value);
    this.updatePlanName(plan);
  }

  deletePlan(plan: FormGroup) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this plan?',
      accept: () => {
        
      }
    });
  }

  // onFloorsChange(plan: FormGroup, event: any) {
  //   plan.get('Floors')?.setValue(event.value);
  // }

  // onGarageChange(plan: FormGroup, event: any) {
  //   plan.get('Garage')?.setValue(event.value);
  // }

  // onAvailableChange(plan: FormGroup, event: any) {
  //   plan.get('Available')?.setValue(event.value);
  // }

  // onArchivedChange(plan: FormGroup, event: any) {
  //   plan.get('Archived')?.setValue(event.value);
  // }

updatePlanName(plan: FormGroup) {
  console.log('triggering updatePlanName');
  const size = plan.get('Size')?.value || '';
  const family = plan.get('Family')?.value || '';
  const suffix = plan.get('Suffix')?.value || null;
  const styleId = plan.get('StyleID')?.value;
  const styleCode = this.styleOptions.find(style => style.StyleID === styleId)?.Code || '';
  console.log('values', size, family, suffix, styleId, styleCode);

  const components = [size, family];
  if (suffix) {
    components.push(suffix);
  }
  components.push(styleCode);
  
  const newName = components
    .filter(Boolean)
    .join('-');
    
  plan.get('PlanName')?.setValue(newName);
}

  createPlan(planData: any): FormGroup {
    return this.fb.group({
      PlanID: [planData.PlanID, Validators.required],
      PlanName: [planData.PlanName, Validators.required],
      Family: [planData.Family, Validators.required],
      Size: [planData.Size, Validators.required],
      Suffix: [planData.Suffix, Validators.required],
      StyleID: [planData.StyleID, Validators.required],
      Collection: [planData.Collection, Validators.required],
      Floors: [planData.Floors, Validators.required],
      Garage: [planData.Garage, Validators.required],
      Available: [planData.Available, Validators.required],
      Archived: [planData.Archived, Validators.required],
      Thumb: [planData.Thumb, Validators.required],
      ThumbPreview: [''],
    });
  }

  get plans(): FormArray {
    return this.planForm.get('plans') as FormArray;
  }

  setDummyFamily() {
    const oldFamily = this.dummyPlan.Family;
    this.dummyPlan.Family = this.planForm.get('family')?.get('familyName')?.value;
    this.dummyPlan.PlanName = this.dummyPlan.PlanName.replace(oldFamily, this.dummyPlan.Family);
  }

  addPlan() {
    const tempId = `temp_${this.tempIdCounter++}`;
    const oldFamily = this.dummyPlan.Family;
    this.dummyPlan.PlanID = tempId;
    this.dummyPlan.Family = this.planForm.get('family')?.get('familyName')?.value;
    this.dummyPlan.PlanName = this.dummyPlan.PlanName.replace(oldFamily, this.dummyPlan.Family);
    this.plans.push(this.createPlan(this.dummyPlan));
  }

  removePlan(index: number) {
    this.plans.removeAt(index);
  }

  async onImageSelect(plan: FormGroup, event: FileUploadHandlerEvent) {
    this.fileProcessing = true;
    const reader = new FileReader();
    let imageUrl = '';

    console.log('onImageSelect', plan);
    console.log('event', event);
    const file = event.files[0];
    if (!file) return;

    reader.onload = (e: any) => {
      // Update preview in the form
      imageUrl = e.target.result;
      plan.get(`ThumbPreview`)?.setValue(imageUrl);
    };

    const planId = plan.get('PlanID')?.value;
    if (!planId) return;

    try {
      // Compression options
      const options = {
        maxSizeMB: 10,
        maxWidthOrHeight: 900,
        useWebWorker: true 
      };

      console.log('compressing file');
  
      // Compress the image
      const compressedFile = await imageCompression(file, options);

      console.log('compressed file');
  
      // Remove any existing pending updates for this plan
      this.pendingImageUpdates = this.pendingImageUpdates.filter(
        update => !(update.PlanID === planId)
      );
  
      // Add new pending update with compressed file
      this.pendingImageUpdates.push({
        PlanID:planId,
        imageFile: compressedFile
      });

      reader.readAsDataURL(compressedFile);
  
  
    } catch (error) {
      console.error('Error compressing image:', error);
    }

    this.fileProcessing = false;

     // Clear the fileupload component
  if (event.files && event.files.length) {
    event.files.length = 0;
  }
  }

  // Modify your existing batch update method to include images
  async batchUpdate() {
    // Your existing batch update logic
    const updates = {}

    // Create FormData to send both JSON and files
    const formData = new FormData();
    formData.append('updates', JSON.stringify(updates));
    
    // Append each pending image
    this.pendingImageUpdates.forEach(update => {
      formData.append(
        'images', 
        update.imageFile, 
        `${update.PlanID}_${update.imageFile.name}`
      );
    });

    try {
      await this.plansDataService.updateBatch(formData);
      this.pendingImageUpdates = []; // Clear pending updates after successful upload
    } catch (error) {
      console.error('Failed to update plans with images:', error);
    }
  }

  onSubmit() {
    console.log(this.planForm.value);
    this.plansDataService.updateBatch(this.planForm.value).subscribe(res => {
      console.log(res);
    });
    
  }

  getImageUrl(plan: FormGroup): string {
    // First check for preview from uploaded image
    const preview = plan.get('ThumbPreview')?.value;
    if (preview) {
      return preview;
    }
    
    // Fall back to original thumb
    return plan.get('Thumb')?.value || '';
  }

}
